import { useState, useRef, useEffect } from 'react';
import NewcommerIcon from '../../../../assets/newcomer-icon.svg';
import ContributorIcon from '../../../../assets/contributor-icon.svg';
import RespectedIcon from '../../../../assets/respected-icon.svg';
import AuthorityIcon from '../../../../assets/authority-icon.svg';
import LuminaryIcon from '../../../../assets/luminary-icon.svg';
import Button from '../../../../components/Button';
import RankBadge from '../../../RankBadge';
import { useNavigate } from 'react-router-dom';
import { useClickOutside } from '../../../../hooks/useOutsideClick';
import SeedIcon from '../../../../assets/seed-icon.svg';
import LeafIcon from '../../../../assets/leaf-icon.svg';
import { getInvestmentPointHistory } from '../../../../apis/article.api';

const MyRankComponent = ({ profile, pointsBalance, pointName, shouldShowMenu = true, height }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [investmentData, setInvestmentData] = useState(null);
  const [loadingInvestments, setLoadingInvestments] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  useClickOutside(menuRef, () => setShowMenu(false));

  // Fetch investment data when menu opens
  useEffect(() => {
    if (showMenu && !investmentData && !loadingInvestments) {
      setLoadingInvestments(true);
      getInvestmentPointHistory({ limit: 1, page: 1 })
        .then((data) => {
          setInvestmentData(data?.data);
        })
        .catch((error) => {
          console.error('Failed to fetch investment data:', error);
        })
        .finally(() => {
          setLoadingInvestments(false);
        });
    }
  }, [showMenu, investmentData, loadingInvestments]);

  const badges = {
    newcomer: {
      icon: NewcommerIcon,
      label: 'Newcomer',
      gradient: 'linear-gradient(90deg, #333333 13%, #6C6C6C 41.5%, #A5A5A5 75.5%)'
    },
    contributor: {
      icon: ContributorIcon,
      label: 'Contributor',
      gradient: 'linear-gradient(90deg, #0054C0 9%, #1E71DB 49.5%, #4697FF 84.5%)'
    },
    respected: {
      icon: RespectedIcon,
      label: 'Respected',
      gradient: 'linear-gradient(90deg, #204A22 18.32%, #459F49 55.58%, #459F49 90.92%)'
    },
    authority: {
      icon: AuthorityIcon,
      label: 'Authority',
      gradient: 'linear-gradient(90deg, #C987DF 12%, #BB54DD 50.5%, #A405D9 92%)'
    },
    luminary: {
      icon: LuminaryIcon,
      label: 'Luminary',
      gradient: 'linear-gradient(180deg, #FF7D4E 6.5%, #FDA704 52%, #FFC15C 92.5%)'
    }
  };

  const badgeInfo = badges[profile?.currentRank?.toLowerCase()] || badges.newcomer;

  return (
    <div className="relative">
      <div
        className="p-px rounded-full hover:opacity-80"
        style={{ background: badgeInfo.gradient, cursor: shouldShowMenu ? 'pointer' : 'default' }}
        onClick={() => setShowMenu(!showMenu)}
      >
        <div
          className={`px-2 py-1 bg-white rounded-full ${height || 'h-[34px] md:h-[38px]'} flex items-center gap-1`}
        >
          <img src={badgeInfo.icon} alt={badgeInfo.label} className="w-4 h-4 md:w-6 md:h-6" />
          <span className="text-xs font-[700] text-dim-gray">
            {pointsBalance || 0}{' '}
            <span className={`${!shouldShowMenu ? 'inline-block' : 'hidden lg:!inline-block'}`}>
              {pointName} Remaining
            </span>
          </span>
        </div>
      </div>

      {/* Menu */}
      {showMenu && shouldShowMenu && (
        <div
          ref={menuRef}
          className="absolute right-[-100px] md:right-0 top-full mt-2 w-[320px] bg-white rounded-[12px] shadow-xl border border-gray-200 p-5 z-10 animate-in slide-in-from-top-2 fade-in duration-200"
          style={{
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            backdropFilter: 'blur(8px)'
          }}
        >
          <div className="space-y-5">
            {/* Rank Section */}
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-700">Your Rank</span>
              <RankBadge rank={profile?.currentRank} />
            </div>

            {/* Twigs Section */}
            <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4 rounded-xl border border-green-100 hover:shadow-sm transition-shadow duration-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1 bg-green-100 rounded-full">
                  <img src={LeafIcon} alt="Twigs" className="w-3 h-3" />
                </div>
                <span className="font-semibold text-green-800">Twigs</span>
                <div className="ml-auto text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full font-medium">
                  Daily
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Daily Allowance:</span>
                  <div className="font-semibold text-green-700">
                    {profile.dailyPointsRemaining} <span className="text-xs font-normal">/ 20</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Lifetime Received:</span>
                  <span className="font-semibold text-green-700">{profile.totalReceivedPoint}</span>
                </div>
              </div>
              <div className="text-xs text-green-600 italic mt-2">Resets daily at midnight UTC</div>
            </div>

            {/* Seeds Section */}
            <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 p-4 rounded-xl border border-amber-100 hover:shadow-sm transition-shadow duration-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1 bg-amber-100 rounded-full">
                  <img src={SeedIcon} alt="Seeds" className="w-3 h-3" />
                </div>
                <span className="font-semibold text-amber-800">Seeds</span>
                <div className="ml-auto text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-medium">
                  Investment
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Available to Invest:</span>
                  <span className="font-semibold text-amber-700">
                    {profile?.totalRawPointsRemaining || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Earned:</span>
                  <span className="font-semibold text-amber-700">{profile.totalReceivedPoint}</span>
                </div>
                {investmentData && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Invested:</span>
                      <span className="font-semibold text-amber-700">
                        {investmentData.totalUserPointsInvested || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Active Investments:</span>
                      <span className="font-semibold text-amber-700">
                        {investmentData.activeArticles || 0}
                      </span>
                    </div>
                  </>
                )}
                {loadingInvestments && (
                  <div className="text-xs text-amber-600 italic">Loading investment data...</div>
                )}
              </div>
              <div className="text-xs text-amber-600 italic mt-2">Earned from received Twigs</div>
            </div>

            {/* Earnings Section */}
            {investmentData?.totalEarned && parseFloat(investmentData.totalEarned) > 0 && (
              <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 p-4 rounded-xl border border-emerald-100 hover:shadow-sm transition-shadow duration-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1 bg-emerald-100 rounded-full">
                    <span className="text-xs">💰</span>
                  </div>
                  <span className="font-semibold text-emerald-800">Earnings</span>
                  <div className="ml-auto text-xs bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded-full font-medium">
                    USD
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Earned:</span>
                  <span className="font-bold text-emerald-700 text-lg">
                    ${investmentData.totalEarned}
                  </span>
                </div>
                <div className="text-xs text-emerald-600 italic mt-2">From your investments</div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="pt-3 border-t border-gray-100">
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="secondary"
                  className="text-xs font-medium py-2.5 hover:bg-gray-100 transition-colors duration-200 flex items-center justify-center gap-1"
                  onClick={() => navigate('/article/manage?tab=investments')}
                >
                  <img src={SeedIcon} alt="Seeds" className="w-3 h-3" />
                  Investments
                </Button>
                <Button
                  variant="secondary"
                  className="text-xs font-medium py-2.5 hover:bg-gray-100 transition-colors duration-200 flex items-center justify-center gap-1"
                  onClick={() => navigate('/article/manage?tab=awarded-twigs')}
                >
                  <img src={LeafIcon} alt="Twigs" className="w-3 h-3" />
                  History
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyRankComponent;
